/**
 * Red Dot v2 Design System Theme
 * Color palette, typography, spacing, and layout constants
 */

export const theme = {
  colors: {
    // Primary colors
    primary: {
      dark: '#265c5a',
      navy: '#265c5a',
      base: '#2d7370',
      ocean: '#2d7370',
      sky: '#3d9692',
      lightSky: '#ccfbf1',
      /** Soft teal hairline — Knowledge Center card/callout borders. */
      mist: '#c9e7e4',
    },
    // Accent colors
    accent: {
      gold: '#4aaca8',
      warm: '#3d9692',
    },
    // Text colors
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      light: '#9ca3af',
      white: '#ffffff',
      /** Long-form body copy (documentation prose) — darker than `secondary`. */
      body: '#374151',
      /** De-emphasised meta text (counts, dates, captions). */
      muted: '#6b7280',
    },
    // Background colors
    backgrounds: {
      white: '#ffffff',
      light: '#f0fdfc',
      /** Muted teal tint — the square behind a Knowledge Center topic icon. */
      tealTint: '#eaf2f1',
      page: '#f9fafb',
      gray: '#f3f4f6',
    },
    // Border colors
    borders: {
      light: '#e5e7eb',
      medium: '#d1d5db',
      dark: '#9ca3af',
      default: '#d1d5db',
    },
    // Status colors
    status: {
      success: {
        bg: '#f0fdf4',
        text: '#16a34a',
        border: '#bbf7d0',
      },
      warning: {
        bg: '#fffbeb',
        text: '#d97706',
        border: '#fde68a',
      },
      warningLight: '#fffbeb',
      error: {
        bg: '#fef2f2',
        text: '#dc2626',
        border: '#fecaca',
      },
      errorLight: '#fef2f2',
      info: {
        bg: '#f0fdfc',
        text: '#2d7370',
        border: '#99f6e4',
      },
      locked: {
        bg: '#f3e8ff',
        text: '#7c3aed',
        border: '#ddd6fe',
      },
    },
    // Role-specific badge colors
    roles: {
      orgAdmin: {
        bg: '#e6e6fa',
        text: '#4b0082',
      },
      supervisor: {
        bg: '#f0fdfc',
        text: '#2d7370',
      },
      technician: {
        bg: '#f0fdf4',
        text: '#16a34a',
      },
    },
    /**
     * Named `gradients` for history: these are deliberately SOLID fills,
     * because the app has to embed cleanly in third-party pages.
     *
     * `knowledgeHero` is the one genuine gradient and the one documented
     * exception — the Knowledge Center (/help) is a standalone documentation
     * surface that is never rendered inside a host app's iframe, so the
     * embedding constraint does not apply to it. Do not follow it as a
     * precedent for anything that can be embedded.
     */
    gradients: {
      sidebar: '#265c5a',
      button: '#2d7370',
      header: '#265c5a',
      knowledgeHero: 'linear-gradient(118deg, #f3faf9 0%, #eaf4f3 46%, #e3f0ef 100%)',
    },
  },

  // Typography — CentralReach-density scale (2026-06-02). Token NAMES unchanged so
  // no component code changes; only rem values shift the type one notch denser.
  // NOTE: the rem anchor stays 16px (GlobalStyles html root) — do NOT change it, or
  // all rem-based spacing/borderRadius/layout collapses. This block only scales text.
  typography: {
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
    lineHeight: {
      tight: 1.25,
      base: 1.45,
      relaxed: 1.6,
    },
    fontSize: {
      xs: '0.6875rem', // 11px
      sm: '0.75rem', // 12px
      base: '0.875rem', // 14px
      lg: '1rem', // 16px
      xl: '1.125rem', // 18px
      '2xl': '1.25rem', // 20px
      '3xl': '1.5rem', // 24px
      '4xl': '1.75rem', // 28px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // Spacing scale (based on 4px grid)
  spacing: {
    0: '0',
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
    12: '3rem', // 48px
    16: '4rem', // 64px
    20: '5rem', // 80px
    24: '6rem', // 96px
  },

  // Layout dimensions
  layout: {
    header: {
      height: '64px',
      background: '#265c5a',
    },
    sidebar: {
      width: '256px',
      widthCollapsed: '64px',
    },
    content: {
      padding: '32px',
      maxWidth: '1440px',
    },
  },

  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.75rem', // 12px
    '2xl': '1rem', // 16px
    full: '9999px',
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    ring: '0 0 0 3px rgba(45, 115, 112, 0.3)',
    ringError: '0 0 0 3px rgba(196, 30, 58, 0.3)',
  },

  // Z-index scale
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    base: '200ms ease-in-out',
    slow: '300ms ease-in-out',
  },

  // Breakpoints
  breakpoints: {
    mobile: '320px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',
  },
} as const;

export type Theme = typeof theme;
